import React__default, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions, Animated, View, Pressable, StyleSheet, Platform } from 'react-native';
import { u as useTheme } from './useTheme-90e8e2c2.esm.js';
import { A as AppInputLabel } from './index-5ae71f5a.esm.js';

function AppSwitch(_ref) {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7;
  let {
    isTicked,
    onChange,
    showTextInputLabel,
    label,
    isMandatory,
    style,
    labelTextStyle,
    wrapperStyle,
    displayIsTicked = false,
    labelStyle,
    activeText = "On",
    inActiveText = "Off",
    disabled,
    size = "medium",
    // small, medium, large
    ...props
  } = _ref;
  const {
    currentTheme,
    mode
  } = useTheme();
  useTranslation();
  const styles = useMemo(() => styleGenerator(currentTheme, size), [currentTheme, size]);
  const {
    width
  } = useWindowDimensions();
  const animatedValue = React__default.useRef(new Animated.Value(isTicked ? 1 : 0)).current;
  React__default.useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: isTicked ? 1 : 0,
      duration: 200,
      useNativeDriver: false
    }).start();
  }, [isTicked, animatedValue]);
  const trackStyle = {
    backgroundColor: animatedValue.interpolate({
      inputRange: [0, 1],
      outputRange: [disabled ? (currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.lightGrey) || (currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.mediumGrey) : currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.mediumGrey, disabled ? currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.darkGrey : currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.primary]
    })
  };
  const thumbStyle = {
    transform: [{
      translateX: animatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [2, styles.track.width - styles.thumb.width - 2]
      })
    }],
    backgroundColor: disabled ? currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.mediumGrey : currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.white
  };
  const handlePress = () => {
    if (!disabled) {
      onChange(!isTicked);
    }
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: [styles.wrapper, wrapperStyle]
  }, showTextInputLabel ? /*#__PURE__*/React__default.createElement(AppInputLabel, {
    labelTextStyle: [labelTextStyle, {
      fontSize: width < 420 && 10
    }],
    style: [{
      paddingRight: displayIsTicked ? 20 : 10,
      flex: 1
    }, labelStyle],
    isMandatory: isMandatory,
    autoMargin: false,
    label: label
  }) : null, /*#__PURE__*/React__default.createElement(Pressable, {
    onPress: handlePress,
    style: [styles.pressable, disabled && styles.disabled],
    disabled: disabled
  }, /*#__PURE__*/React__default.createElement(Animated.View, {
    style: [styles.track, trackStyle]
  }, /*#__PURE__*/React__default.createElement(Animated.View, {
    style: [styles.thumb, thumbStyle]
  }))));
}
const getSizeConfig = size => {
  switch (size) {
    case "small":
      return {
        trackWidth: 32,
        trackHeight: 18,
        thumbSize: 14
      };
    case "large":
      return {
        trackWidth: 56,
        trackHeight: 32,
        thumbSize: 28
      };
    default:
      // medium
      return {
        trackWidth: 44,
        trackHeight: 24,
        thumbSize: 20
      };
  }
};
const styleGenerator = (props, size) => {
  const config = getSizeConfig(size);
  return StyleSheet.create({
    wrapper: {
      justifyContent: "space-between",
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 20,
      display: "flex",
      borderRadius: 10
    },
    pressable: {
      cursor: Platform.OS === "web" ? "pointer" : "default",
      opacity: 1
    },
    disabled: {
      cursor: Platform.OS === "web" ? "not-allowed" : "default",
      opacity: 0.6
    },
    track: {
      width: config.trackWidth,
      height: config.trackHeight,
      borderRadius: config.trackHeight / 2,
      justifyContent: "center",
      position: "relative",
      ...Platform.select({
        web: {
          transition: "background-color 0.2s ease"
        }
      })
    },
    thumb: {
      width: config.thumbSize,
      height: config.thumbSize,
      borderRadius: config.thumbSize / 2,
      position: "absolute",
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 1
      },
      shadowOpacity: 0.2,
      shadowRadius: 2,
      elevation: 2,
      ...Platform.select({
        web: {
          transition: "transform 0.2s ease, background-color 0.2s ease",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
        }
      })
    }
  });
};
AppSwitch.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AppSwitch",
  "props": {
    "displayIsTicked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "required": false
    },
    "activeText": {
      "defaultValue": {
        "value": "\"On\"",
        "computed": false
      },
      "required": false
    },
    "inActiveText": {
      "defaultValue": {
        "value": "\"Off\"",
        "computed": false
      },
      "required": false
    },
    "size": {
      "defaultValue": {
        "value": "\"medium\"",
        "computed": false
      },
      "required": false
    }
  }
};

export { AppSwitch as A };
